import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import {
  Bq4SafetyManagementM,
  initialBq4SafetyManagement,
  mapSqlToBq4SafetyManagementForm,
} from "../models/bq4-safety-management.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF4SafetyManagementAtom } from "../state/baltic-form.atoms";
import { InputCell } from "../services/baltic-forms.utils";
import { useAtom, useAtomValue } from "jotai";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq4SafetyManagement: React.FC<Props> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF4SafetyManagementAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq4SafetyManagementM>(initialBq4SafetyManagement);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/bq_SafetyManagement`, {
        params: {
          imo_no: imoNumber,
          vessel_name: vesselName,
        },
      });
      const _formSql = result.data[0];
      const _form = mapSqlToBq4SafetyManagementForm(_formSql);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg" loadingIndicatorClassName="">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-4">Safety Management</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">Document of Compliance (DOC)</label>
              <p className="text-xs font-medium">(Does vessel have a valid DOC?)</p>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="docValid"
                    value="yes"
                    checked={form.docValid === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="docValid"
                    value="no"
                    checked={form.docValid === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("docValidComments")}
                >
                  Comments
                </button>
              </label>
              {(form.docValidComments || isCommentsVisible("docValidComments")) && (
                <textarea
                  disabled={readonly}
                  name="docValidComments"
                  value={form.docValidComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">Document of Compliance (DOC)</label>
              <p className="text-sm font-medium">certificate number</p>
              <input
                disabled={readonly}
                type="text"
                name="docCertificateNumber"
                value={form.docCertificateNumber}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">Document of Compliance (DOC)</label>
              <p className="text-sm font-medium">issuing authority</p>
              <select
                disabled={readonly}
                name="docIssuingAuthority"
                value={form.docIssuingAuthority}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              >
                <option value=""></option>
                <option value="AMERICAN BUREAU OF SHIPPING">ABS</option>
                <option value="BUREAU VERITAS">BV</option>
                <option value="CHINA CLASSIFICATION SOCIETY">China Register</option>
                <option value="DET NORSK VERITAS">DNV</option>
                <option value="DNV GL">DNV GL</option>
                <option value="GERMANISHER LLOYDS">Germanisher Lloyds</option>
                <option value="KOREAN REGISTER OF SHIPPING">Korea Register</option>
                <option value="LLOYD'S REGISTER OF SHIPPING">Lloyd's Register</option>
                <option value="NIPON KIJI KYOKAY">NKK</option>
                <option value="RINA- REGISTO ITALIANO NAVAL">RINA</option>
                <option value="RUSSIAN MARITIME REGISTER OF SHIPPING">Russian Register</option>
              </select>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">ISM Safety Management Certificate (SMC) </label>
              <p className="text-sm font-medium">certificate number</p>
              <input
                disabled={readonly}
                type="text"
                name="smcCertificateNumber"
                value={form.smcCertificateNumber}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">ISM Safety Management Certificate (SMC)</label>
              <p className="text-sm font-medium">certifying authority</p>
              <select
                disabled={readonly}
                name="smcCertifyingAuthority"
                value={form.smcCertifyingAuthority}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              >
                <option value=""></option>
                <option value="AMERICAN BUREAU OF SHIPPING">ABS</option>
                <option value="BUREAU VERITAS">BV</option>
                <option value="CHINA CLASSIFICATION SOCIETY">China Register</option>
                <option value="DET NORSK VERITAS">DNV</option>
                <option value="DNV GL">DNV GL</option>
                <option value="GERMANISHER LLOYDS">Germanisher Lloyds</option>
                <option value="KOREAN REGISTER OF SHIPPING">Korea Register</option>
                <option value="LLOYD'S REGISTER OF SHIPPING">Lloyd's Register</option>
                <option value="NIPON KIJI KYOKAY">NKK</option>
                <option value="RINA- REGISTO ITALIANO NAVAL">RINA</option>
                <option value="RUSSIAN MARITIME REGISTER OF SHIPPING">Russian Register</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 mt-4">
              <label className="block text-sm font-bold">
                If the vessel is subject to any conditions of class, class extensions, outstanding memorandums or class
                recommendations, give details:
              </label>
              <textarea
                disabled={readonly}
                name="vesselSubjectClass"
                value={form.vesselSubjectClass}
                onChange={handleChange}
                rows={3}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">Is vessel operated under a Quality Management System?</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="operatedUnderQMS"
                    value="yes"
                    checked={form.operatedUnderQMS === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="operatedUnderQMS"
                    value="no"
                    checked={form.operatedUnderQMS === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>

            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">
                If vessel is operating under a Quality management system, what type of system?
              </label>
              <p className="text-xs font-medium">ISO9001 or IMO Resolution A.741(18)?</p>
              <select
                disabled={readonly}
                name="qmsType"
                value={form.qmsType}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              >
                <option value=""></option>
                <option value="ISO9002">ISO9002</option>
                <option value="IMO Resolution A.741(18)">IMO Resolution A.741(18)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">
                Does the vessel comply with the ICS Guide to Helicopter/Ship Operations?:
              </label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="icsGuideComply"
                    value="yes"
                    checked={form.icsGuideComply === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="icsGuideComply"
                    value="no"
                    checked={form.icsGuideComply === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>

            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">
                If Yes, state whether winching or landing area provided:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="winchOrLandingArea"
                value={form.winchOrLandingArea}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1 mb-4">
              <label className="block text-sm font-bold">What is diameter of the circle provided?:</label>
              <InputCell
                disabled={readonly}
                name="circleDiameter"
                value={form.circleDiameter}
                onChange={handleChange}
                trailingText="Meters"
                inputType="number"
              />
            </div>
          </div>

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
